import React from "react";
import { Badge, Button, Icon, IconButton, Input, Logo, Select } from "../components/DesignSystem";
import type { AuthSession, PlannerModuleKey, PlannerWorkspace, Scope, WeddingEvent } from "../domain/types";
import { formatShortDate, routeLabels } from "../screens/common";
import { usePlannerData } from "../state/PlannerDataContext";
import { GlobalSearchDialog } from "./GlobalSearchDialog";

export type RouteKey = "home" | "onboarding" | "events" | "tasks" | "calendar" | "runsheets" | "dayof" | "budget" | "payments" | "vendors" | "venues" | "guests" | "households" | "rsvp" | "invitations" | "seating" | "stationery" | "website" | "moodboards" | "outfits" | "beauty" | "photography" | "gifts" | "giftsreceived" | "menu" | "travel" | "transport" | "import" | "updates" | "documents" | "security" | "collaborators" | "admin" | "invitation-brief" | "more";

const routeModules: Partial<Record<RouteKey, PlannerModuleKey>> = {
  events: "events",
  tasks: "tasks",
  calendar: "calendar",
  runsheets: "runsheets",
  dayof: "dayof",
  budget: "budget",
  payments: "payments",
  vendors: "vendors",
  venues: "venues",
  guests: "guests",
  households: "households",
  rsvp: "rsvp",
  invitations: "invitations",
  seating: "seating",
  stationery: "stationery",
  website: "website",
  moodboards: "moodboards",
  outfits: "outfits",
  beauty: "beauty",
  photography: "photography",
  gifts: "gifts",
  giftsreceived: "gifts_received",
  menu: "menu",
  travel: "travel",
  transport: "transport",
  import: "guest_import",
  documents: "documents",
  collaborators: "collaborators"
};

const desktopSections: Array<{ label: string; items: Array<{ id: RouteKey; label: string; icon: string }> }> = [
  { label: "Home", items: [{ id: "home", label: "Overview", icon: "layout-dashboard" }] },
  { label: "Plan", items: [
    { id: "events", label: "Events", icon: "calendar-days" },
    { id: "tasks", label: "Tasks", icon: "square-check" },
    { id: "calendar", label: "Calendar", icon: "calendar-days" },
    { id: "runsheets", label: "Run sheets", icon: "list-checks" }
  ] },
  { label: "Money", items: [
    { id: "budget", label: "Budget", icon: "wallet" },
    { id: "payments", label: "Payments", icon: "receipt" }
  ] },
  { label: "Vendors", items: [
    { id: "vendors", label: "Vendors", icon: "store" },
    { id: "venues", label: "Venues", icon: "map-pin" }
  ] },
  { label: "Guests", items: [
    { id: "guests", label: "Guest list", icon: "users" },
    { id: "households", label: "Households", icon: "house" },
    { id: "rsvp", label: "RSVP", icon: "mail-check" },
    { id: "invitations", label: "Invitations", icon: "mail" },
    { id: "seating", label: "Seating", icon: "layout-grid" },
    { id: "import", label: "Import guests", icon: "file-spreadsheet" }
  ] },
  { label: "Design", items: [
    { id: "stationery", label: "Stationery", icon: "mail-open" },
    { id: "website", label: "Wedding website", icon: "globe" },
    { id: "moodboards", label: "Moodboards", icon: "image" }
  ] },
  { label: "Logistics", items: [
    { id: "menu", label: "Food & menu", icon: "utensils" },
    { id: "travel", label: "Travel & stay", icon: "plane" },
    { id: "transport", label: "Transport", icon: "car" }
  ] },
  { label: "More", items: [
    { id: "outfits", label: "Outfits", icon: "shirt" },
    { id: "beauty", label: "Beauty", icon: "sparkles" },
    { id: "photography", label: "Photography", icon: "camera" },
    { id: "gifts", label: "Gifts & favours", icon: "gift" },
    { id: "giftsreceived", label: "Gifts received", icon: "lock" }
  ] },
  { label: "Account", items: [
    { id: "updates", label: "Updates", icon: "bell" },
    { id: "documents", label: "Documents", icon: "folder" },
    { id: "collaborators", label: "Collaborators", icon: "users" },
    { id: "security", label: "Security", icon: "lock" },
    { id: "invitation-brief", label: "Invitation Brief", icon: "mail" },
    { id: "onboarding", label: "Setup walkthrough", icon: "sparkle" },
    { id: "admin", label: "Admin", icon: "shield-check" }
  ] }
];

export function isAdminSession(session: AuthSession | null | undefined): boolean {
  return Boolean(session?.user.adminRoleTier);
}

/** Whether a route shows in nav at all. Admin/onboarding stay admin-only (real access-control
 * gates, not commercial ones). Every other route always shows now, even when its module isn't
 * entitled — POST-BUILD FEATURE — customer access §5: "Do not hide premium capabilities
 * completely... show them in navigation with a subtle lock" rather than the previous behavior of
 * hiding a module entirely until it was purchased. `routeLocked`, below, is what actually reflects
 * entitlement — the frontend server call still enforces this for real, this is presentation only. */
export function routeVisibleInShell(route: RouteKey, _enabledModules: PlannerModuleKey[], session: AuthSession | null | undefined): boolean {
  if (route === "admin" || route === "onboarding") return isAdminSession(session);
  return true;
}

/** Whether this route's module isn't entitled — drives the nav's lock badge and whether opening
 * it shows the real screen or the locked-module upsell instead (App.tsx's RouteView). */
export function routeLocked(route: RouteKey, enabledModules: PlannerModuleKey[]): boolean {
  const module = routeModules[route];
  return Boolean(module) && !enabledModules.includes(module as PlannerModuleKey);
}

export const mobileRouteMap: Record<RouteKey, "home" | "plan" | "guests" | "budget" | "more"> = {
  home: "home",
  onboarding: "more",
  events: "plan",
  tasks: "plan",
  calendar: "plan",
  runsheets: "plan",
  dayof: "more",
  budget: "budget",
  payments: "budget",
  vendors: "more",
  venues: "more",
  guests: "guests",
  households: "guests",
  rsvp: "guests",
  invitations: "guests",
  seating: "more",
  stationery: "more",
  website: "more",
  moodboards: "more",
  outfits: "more",
  beauty: "more",
  photography: "more",
  gifts: "more",
  giftsreceived: "more",
  menu: "more",
  travel: "more",
  transport: "more",
  import: "guests",
  updates: "home",
  documents: "more",
  security: "more",
  collaborators: "more",
  admin: "more",
  "invitation-brief": "more",
  more: "more"
};

export function PlannerShell({ route, setRoute, scope, setScope, children }: {
  route: RouteKey;
  setRoute: (route: RouteKey) => void;
  scope: Scope;
  setScope: (scope: Scope) => void;
  children: React.ReactNode;
}) {
  const { workspace } = usePlannerData();
  const [isMobile, setIsMobile] = React.useState(() => window.matchMedia("(max-width: 860px)").matches);
  React.useEffect(() => {
    const query = window.matchMedia("(max-width: 860px)");
    const onChange = () => setIsMobile(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  if (!workspace) return <>{children}</>;
  if (isMobile) return <MobileShell route={route} setRoute={setRoute} scope={scope} setScope={setScope} events={workspace.events}>{children}</MobileShell>;
  return <DesktopShell route={route} setRoute={setRoute} scope={scope} setScope={setScope} events={workspace.events}>{children}</DesktopShell>;
}

function DesktopShell({ route, setRoute, scope, setScope, events, children }: { route: RouteKey; setRoute: (route: RouteKey) => void; scope: Scope; setScope: (scope: Scope) => void; events: WeddingEvent[]; children: React.ReactNode }) {
  const { workspace, session, signOut, switchWedding } = usePlannerData();
  const [searchOpen, setSearchOpen] = React.useState(false);
  const unread = workspace?.notifications.filter((item) => !item.read).length || 0;
  const enabledModules = workspace?.wedding.enabledModules || [];
  const initials = (session?.user.name || session?.user.email || "POB").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="pob-app" style={{ display: "flex", overflow: "hidden" }}>
      <nav style={{ display: "flex", flexDirection: "column", width: "var(--sidebar-width)", flex: "0 0 auto", background: "var(--surface-page)", borderRight: "1px solid var(--border-subtle)" }}>
        <div style={{ padding: "var(--space-5) var(--space-5) var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {workspace && <WeddingIdentity workspace={workspace} session={session} onSwitch={switchWedding} />}
          <EventScopeSelect scope={scope} setScope={setScope} events={events} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "0 var(--space-3) var(--space-4)" }}>
          {desktopSections.map((section) => (
            <div key={section.label} style={{ marginBottom: "var(--space-6)" }}>
              <div className="pob-eyebrow" style={{ padding: "0 var(--space-3) var(--space-2)", color: "var(--text-tertiary)" }}>{section.label}</div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                {section.items.filter((item) => routeVisibleInShell(item.id, enabledModules, session)).map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => setRoute(item.id)} style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-3)",
                      width: "100%",
                      minHeight: 30,
                      padding: "0 var(--space-3)",
                      border: 0,
                      borderRadius: "var(--radius-md)",
                      background: item.id === route ? "var(--surface-card)" : "transparent",
                      boxShadow: item.id === route ? "var(--shadow-hairline)" : "none",
                      color: item.id === route ? "var(--text-primary)" : "var(--text-secondary)",
                      font: "var(--type-body)",
                      fontSize: "var(--text-sm)",
                      textAlign: "left",
                      cursor: "pointer"
                    }}>
                      <Icon name={item.icon} size={15} style={{ color: item.id === route ? "var(--text-accent)" : "var(--text-tertiary)" }} />
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {routeLocked(item.id, enabledModules) && <Icon name="lock" size={12} style={{ color: "var(--text-tertiary)" }} />}
                      {item.id === "updates" && unread > 0 && <span className="pob-tnum" style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>{unread}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "var(--space-4) var(--space-5)", borderTop: "1px solid var(--border-subtle)" }}>
          <button type="button" onClick={() => setRoute("updates")} style={{ display: "flex", alignItems: "center", gap: 9, flex: 1, minWidth: 0, border: 0, background: "transparent", cursor: "pointer", padding: 0 }}>
            <span style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--ivory-500)", display: "flex", alignItems: "center", justifyContent: "center", font: "var(--type-eyebrow)", color: "var(--charcoal-900)" }}>{initials}</span>
            <span style={{ flex: 1, font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-secondary)", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session?.user.name || session?.user.email} · {session?.user.role.replace("_", " ")}</span>
          </button>
          <IconButton icon="log-out" label="Sign out" size="sm" onClick={() => { void signOut(); }} />
        </div>
      </nav>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", height: "var(--topbar-height)", padding: "0 var(--gutter)", borderBottom: "1px solid var(--border-subtle)", background: "var(--surface-page)" }}>
          <button type="button" onClick={() => setSearchOpen(true)} style={{ width: 300, border: 0, background: "transparent", padding: 0, cursor: "pointer" }}>
            <Input size="sm" icon="search" placeholder="Search guests, households, vendors..." readOnly style={{ width: "100%", cursor: "pointer" }} />
          </button>
          <span style={{ flex: 1 }} />
          {scope !== "all" && <Badge tone="accent">Scoped to {scopeLabel(scope, events)}</Badge>}
          <IconButton icon="bell" label="Updates" onClick={() => setRoute("updates")} />
        </div>
        <main style={{ flex: 1, overflowY: "auto", position: "relative" }}>{children}</main>
      </div>
      {searchOpen && <GlobalSearchDialog onClose={() => setSearchOpen(false)} onNavigate={setRoute} />}
    </div>
  );
}

function WeddingIdentity({ workspace, session, onSwitch }: { workspace: PlannerWorkspace; session: AuthSession | null; onSwitch: (weddingId: string) => Promise<void> }) {
  const choices = session?.availableWeddings || [];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <Logo height={24} />
      {choices.length > 1 ? (
        <Select
          size="sm"
          value={workspace.wedding.id}
          options={choices.map((choice) => ({ value: choice.weddingId, label: choice.title }))}
          onChange={(event) => { void onSwitch(event.target.value); }}
          style={{ flex: 1, minWidth: 0 }}
        />
      ) : (
        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{ font: "var(--type-label)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{workspace.wedding.coupleName}</span>
          <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>{workspace.wedding.datesLabel}</span>
        </span>
      )}
    </div>
  );
}

function MobileShell({ route, setRoute, scope, setScope, events, children }: { route: RouteKey; setRoute: (route: RouteKey) => void; scope: Scope; setScope: (scope: Scope) => void; events: WeddingEvent[]; children: React.ReactNode }) {
  const [switching, setSwitching] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const { workspace, session, switchWedding } = usePlannerData();
  const weddingChoices = session?.availableWeddings || [];
  const active = mobileRouteMap[route] || "more";
  const tabs: Array<[typeof active, string, string, RouteKey]> = [
    ["home", "Home", "layout-dashboard", "home"],
    ["plan", "Plan", "square-check", "tasks"],
    ["budget", "Budget", "wallet", "budget"],
    ["guests", "Guests", "users", "guests"],
    ["more", "More", "ellipsis", "more"]
  ];
  return (
    <div className="pob-app" style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "var(--ivory-300)" }}>
      <div className="pob-mobile-shell">
        <div style={{ flex: "0 0 auto", padding: "var(--space-4) var(--space-4) var(--space-3)", borderBottom: "1px solid var(--border-subtle)", background: "var(--surface-page)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            {weddingChoices.length > 1 && workspace ? (
              <Select
                size="sm"
                value={workspace.wedding.id}
                options={weddingChoices.map((choice) => ({ value: choice.weddingId, label: choice.title }))}
                onChange={(event) => { void switchWedding(event.target.value); }}
                style={{ flex: 1, minWidth: 0 }}
              />
            ) : (
              <span style={{ font: "var(--type-label)", flex: 1 }}>{workspace?.wedding.coupleName || "Pockets of Beige"}</span>
            )}
            <IconButton icon="search" label="Search" size="sm" onClick={() => setSearchOpen(true)} />
            <IconButton icon="bell" label="Updates" size="sm" onClick={() => setRoute("updates")} />
          </div>
          <button type="button" onClick={() => setSwitching(true)} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: "var(--space-2)", padding: 0, border: 0, background: "transparent", cursor: "pointer", font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>
            {scopeLabel(scope, events)}
            <Icon name="chevron-down" size={13} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "var(--space-4)" }}>{children}</div>
        <div style={{ flex: "0 0 auto", display: "grid", gridTemplateColumns: "repeat(5, 1fr)", borderTop: "1px solid var(--border-subtle)", background: "var(--surface-card)" }}>
          {tabs.map(([id, label, icon, target]) => {
            const on = active === id;
            return (
              <button key={id} type="button" onClick={() => setRoute(target)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minHeight: 56, padding: "9px 0", border: 0, background: "transparent", cursor: "pointer", color: on ? "var(--text-primary)" : "var(--text-tertiary)" }}>
                <Icon name={icon} size={17} style={{ color: on ? "var(--text-accent)" : "var(--text-tertiary)" }} />
                <span style={{ font: "var(--type-eyebrow)", fontSize: 10, letterSpacing: "0.06em" }}>{label}</span>
              </button>
            );
          })}
        </div>
        {switching && <MobileScopeSheet scope={scope} setScope={setScope} events={events} onClose={() => setSwitching(false)} />}
        {searchOpen && <GlobalSearchDialog onClose={() => setSearchOpen(false)} onNavigate={setRoute} />}
      </div>
    </div>
  );
}

function EventScopeSelect({ scope, setScope, events }: { scope: Scope; setScope: (scope: Scope) => void; events: WeddingEvent[] }) {
  const [open, setOpen] = React.useState(false);
  const options = [{ id: "all", name: "Whole wedding", sub: `${events.length} celebrations` }, ...events.map((event) => ({ id: event.id, name: event.name, sub: `${formatShortDate(event.date)} · ${event.venue}` })), { id: "shared", name: "Shared costs", sub: "Whole-wedding records" }];
  return (
    <div style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", width: "100%", minHeight: 34, padding: "0 10px", background: "var(--surface-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-raised)", cursor: "pointer", font: "var(--type-label)", textAlign: "left" }}>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{scopeLabel(scope, events)}</span>
        <Icon name="chevron-down" size={13} style={{ color: "var(--text-tertiary)" }} />
      </button>
      {open && <div style={{ position: "absolute", top: 38, left: 0, right: 0, zIndex: 30, background: "var(--surface-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-overlay)", overflow: "hidden" }}>
        {options.map((option) => (
          <button key={option.id} type="button" onClick={() => { setScope(option.id); setOpen(false); }} style={{ display: "block", width: "100%", padding: "9px 11px", textAlign: "left", border: 0, borderBottom: "1px solid var(--divider)", cursor: "pointer", background: option.id === scope ? "var(--surface-selected)" : "transparent" }}>
            <span style={{ font: "var(--type-label)", display: "block" }}>{option.name}</span>
            <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>{option.sub}</span>
          </button>
        ))}
      </div>}
    </div>
  );
}

function MobileScopeSheet({ scope, setScope, events, onClose }: { scope: Scope; setScope: (scope: Scope) => void; events: WeddingEvent[]; onClose: () => void }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", background: "rgba(29,29,27,.28)", backdropFilter: "blur(2px)", zIndex: 40 }} onClick={onClose}>
      <div onClick={(event) => event.stopPropagation()} style={{ background: "var(--surface-card)", borderTopLeftRadius: "var(--radius-lg)", borderTopRightRadius: "var(--radius-lg)", padding: "var(--space-4) 0 var(--space-2)" }}>
        <span className="pob-eyebrow" style={{ display: "block", padding: "0 var(--space-5) var(--space-3)" }}>Viewing</span>
        {[{ id: "all", name: "Whole wedding", sub: `${events.length} celebrations` }, ...events.map((event) => ({ id: event.id, name: event.name, sub: `${formatShortDate(event.date)} · ${event.venue}` }))].map((option) => (
          <button key={option.id} type="button" onClick={() => { setScope(option.id); onClose(); }} style={{ display: "block", width: "100%", minHeight: 52, padding: "var(--space-3) var(--space-5)", textAlign: "left", border: 0, borderTop: "1px solid var(--divider)", cursor: "pointer", background: option.id === scope ? "var(--surface-selected)" : "transparent" }}>
            <span style={{ font: "var(--type-label)", display: "block" }}>{option.name}</span>
            <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>{option.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function scopeLabel(scope: Scope, events: WeddingEvent[]): string {
  if (scope === "all") return "Whole wedding";
  if (scope === "shared") return "Shared costs";
  return events.find((event) => event.id === scope)?.name || String(scope);
}

export function MobileMore({ setRoute }: { setRoute: (route: RouteKey) => void }) {
  const { workspace, session, signOut } = usePlannerData();
  const enabledModules = workspace?.wedding.enabledModules || [];
  const rows: RouteKey[] = ["events", "calendar", "runsheets", "dayof", "payments", "vendors", "venues", "households", "rsvp", "invitations", "seating", "stationery", "website", "moodboards", "outfits", "beauty", "photography", "gifts", "giftsreceived", "menu", "travel", "transport", "import", "updates", "documents", "security", "collaborators", "invitation-brief", "onboarding", "admin"].filter((route) => routeVisibleInShell(route, enabledModules, session));
  return (
    <div>
      <h1 style={{ font: "var(--type-section)", margin: "0 0 var(--space-4)" }}>More</h1>
      {rows.map((route) => (
        <div key={route} className="pob-row" onClick={() => setRoute(route)} style={{ cursor: "pointer" }}>
          <span style={{ flex: 1, font: "var(--type-body)", fontSize: "var(--text-sm)" }}>{routeLabels[route]}</span>
          {routeLocked(route, enabledModules) && <Icon name="lock" size={12} style={{ color: "var(--text-tertiary)", marginRight: 6 }} />}
          <span style={{ color: "var(--text-tertiary)" }}>›</span>
        </div>
      ))}
      <div className="pob-row" onClick={() => { void signOut(); }} style={{ cursor: "pointer" }}>
        <span style={{ flex: 1, font: "var(--type-body)", fontSize: "var(--text-sm)" }}>Sign out</span>
        <span style={{ color: "var(--text-tertiary)" }}>›</span>
      </div>
    </div>
  );
}
